import { computed, inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { SynopticStandardUtils } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-standard-color.utils';
import { SelectedNode } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { DefinitionColumn } from '@connect-services/client/models/export-xlsx.model';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { OpticalPathDto, SynopticService } from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

interface OpticalPathExtended extends OpticalPathDto {
    route?: OpticalPathRoute[];
}

interface OpticalPathRoute {
    bo?: string;
    cb?: string;
    fo?: number;
    tb?: number;
    tc?: string;
}

export class SpecificFiber {
    public cassette!: string;
    public fiberPosition!: string;
    public incomingFiber?: string;
    public outgoingFiber?: string;
    public positionId?: string;
}

const DEFAULT_COLUMN_WIDTH = 15;

@Injectable({
    providedIn: 'root',
})
export class OpticalPathExportService {
    public readonly projectId = computed(() => this.projectBloc.value()?.id || '');

    protected readonly projectBloc = inject(ProjectBloc);

    private readonly columnToColor: ReadonlyArray<string> = ['fo', 'tb'];
    private readonly defaultColumnWidth: number = DEFAULT_COLUMN_WIDTH;
    private readonly opticalColumns: ReadonlyArray<DefinitionColumn<keyof OpticalPathExtended>>;
    private readonly opticalRouteColumns: ReadonlyArray<DefinitionColumn<keyof OpticalPathRoute>>;
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly snackbarService = inject(SnackBarService);
    private readonly synopticService = inject(SynopticService);
    private readonly translateService = inject(TranslateService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.opticalColumns = [
            { header: this.translocoService.translate('opticalPath.upstreamObject'), key: 'st_or_pt_amont', width: 20 },
            { header: this.translocoService.translate('opticalPath.upstreamBayEBP'), key: 'baie_or_ebp', width: 24 },
            { header: this.translocoService.translate('opticalPath.drawerLabel'), key: 'ti_etiquet', width: 24 },
            { header: this.translocoService.translate('opticalPath.drawerPlacement'), key: 'tiroir', width: 20 },
            { header: this.translocoService.translate('opticalPath.cassette'), key: 'cassette', width: 10 },
            { header: this.translocoService.translate('opticalPath.position'), key: 'position', width: 10 },
            {
                header: this.translocoService.translate('opticalPath.connectionType'),
                key: 'type_connection',
                width: this.defaultColumnWidth,
            },
            { header: this.translocoService.translate('opticalPath.OPAssignment'), key: 'affect_ro', width: 20 },
        ];

        this.opticalRouteColumns = [
            { header: this.translocoService.translate('opticalPath.cable'), key: 'cb', width: 16 },
            { header: this.translocoService.translate('opticalPath.tube'), key: 'tb', width: 8 },
            { header: this.translocoService.translate('opticalPath.fiber'), key: 'fo', width: 8 },
            { header: this.translocoService.translate('opticalPath.box'), key: 'bo', width: 16 },
            { header: this.translocoService.translate('opticalPath.connectionType'), key: 'tc', width: 16 },
        ];
    }

    public async exportOpticalPath(
        depositId: string,
        entityId: string,
        businessIdentifier: string,
        specificFiberInformation?: SpecificFiber,
    ): Promise<void> {
        const opticalPath: OpticalPathExtended[] = await this.getOpticalPath(
            depositId,
            entityId,
            specificFiberInformation,
        );

        if (!opticalPath?.length) {
            this.snackbarService.openErrorSnackBar('noOpticalPathFound');

            return;
        }

        const fileName: string = this.buildFileName(businessIdentifier, specificFiberInformation);

        await this.buildExcel(opticalPath, fileName);

        return;
    }

    public findBusinessIdentifier(node: SelectedNode): string {
        if (node.dataNode.boxLabel) {
            return node.dataNode.boxLabel;
        }

        if (node.dataNode.codeExt) {
            return node.dataNode.codeExt;
        }

        return node.dataNode.label;
    }

    private addDataToSheet(sheet: Excel.Worksheet, opticalPaths: OpticalPathExtended[]): void {
        opticalPaths.toSorted(this.sortOpticalPath).forEach((row: OpticalPathExtended): void => {
            const rowValues: Record<string, unknown> = {};

            this.opticalColumns.forEach((col: DefinitionColumn<keyof OpticalPathExtended>): void => {
                rowValues[col.key] = row[col.key] ?? '';
            });

            row.route?.forEach((routeItem: OpticalPathRoute, idx: number): void => {
                this.opticalRouteColumns.forEach((col: DefinitionColumn<keyof OpticalPathRoute>): void => {
                    const key = `${col.key}_${idx}`;
                    rowValues[key] = routeItem[col.key] ?? '';
                });
            });

            const addedRow: Excel.Row = sheet.addRow(rowValues);

            this.colorFiberAndTubeCells(addedRow);
        });
    }

    private addSeparatorColumnBorder(sheet: Excel.Worksheet): void {
        const separatorColIndex = this.opticalColumns.length;

        sheet.eachRow({ includeEmpty: true }, (row: Excel.Row): void => {
            const cell: Excel.Cell = row.getCell(separatorColIndex);

            cell.border = {
                ...cell.border,
                right: { style: 'medium' },
            };
        });
    }

    private async buildExcel(opticalPaths: OpticalPathExtended[], fileName: string): Promise<void> {
        try {
            const workbook = new Excel.Workbook();

            const sheet: Excel.Worksheet = workbook.addWorksheet(this.translocoService.translate('opticalPath.OP'));

            this.buildSheetColumns(sheet, opticalPaths);
            this.addDataToSheet(sheet, opticalPaths);
            this.addSeparatorColumnBorder(sheet);

            const buffer = await workbook.xlsx.writeBuffer();

            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            saveAs(blob, `${fileName}.xlsx`);

            this.snackbarService.openSuccessSnackBar('exportSuccess');
        } catch {
            this.snackbarService.openSuccessSnackBar('dataExportFailed');
        }
    }

    private buildFileName(entityBusinessIdentifier: string, specificFiberInformation?: SpecificFiber): string {
        const projectName: string = this.projectBloc.value()!.name;
        const date: string = this.translateService.getFormattedLocaleDate(new Date()).replace(/\//g, '_');
        const sanitizedBusinessIdentifier = this.sanitizeFileName(entityBusinessIdentifier);

        if (!specificFiberInformation) {
            return `${projectName}-${sanitizedBusinessIdentifier}-${date}`;
        }

        return `${projectName}-${sanitizedBusinessIdentifier}-Cassette${specificFiberInformation!.cassette}-Position${specificFiberInformation!.fiberPosition}-${date}`;
    }

    private buildSheetColumns(sheet: Excel.Worksheet, opticalPaths: OpticalPathDto[]): void {
        const baseColumns: DefinitionColumn[] = this.opticalColumns.map(
            (column: DefinitionColumn): DefinitionColumn => ({
                header: column.header,
                key: column.key,
                width: column.width ?? this.defaultColumnWidth,
            }),
        );

        const maxRoutes: number = Math.max(
            ...opticalPaths.map((opticalPath: OpticalPathDto): number => opticalPath.route?.length ?? 0),
        );

        const dynamicRouteColumns: Partial<Excel.Column>[] = [];

        for (let i = 0; i < maxRoutes; i++) {
            this.opticalRouteColumns.forEach((column: DefinitionColumn): void => {
                dynamicRouteColumns.push({
                    header: `${column.header} ${i + 1}`,
                    key: `${column.key}_${i}`,
                    width: column.width ?? this.defaultColumnWidth,
                });
            });
        }

        sheet.columns = [...baseColumns, ...dynamicRouteColumns];
    }

    private colorFiberAndTubeCells(row: Excel.Row): void {
        row.eachCell({ includeEmpty: true }, (cell: Excel.Cell, colNumber: number): void => {
            const key: string | undefined = row.worksheet.getColumn(colNumber).key;

            if (key && this.columnToColor.some((base: string): boolean => key.startsWith(base))) {
                const value: Excel.CellValue = cell.value;

                const color: string = SynopticStandardUtils.formatColor(Number(value));

                cell.fill = {
                    fgColor: { argb: color.slice(1) },
                    pattern: 'solid',
                    type: 'pattern',
                };

                if (color.toUpperCase() === '#000000') {
                    cell.font = {
                        color: { argb: 'FFFFFF' },
                    };
                }
            }
        });
    }

    private getOpticalPath(
        depositId: string,
        entityId?: string,
        specificFiberInformation?: SpecificFiber,
    ): Promise<OpticalPathDto[]> {
        if (
            specificFiberInformation &&
            !specificFiberInformation.incomingFiber &&
            !specificFiberInformation.outgoingFiber
        ) {
            throw new Error('Specific fiber information must include at least one of incomingFiber or outgoingFiber.');
        }

        return firstValueFrom(
            this.synopticService.synopticControllerGetOpticalPath({
                depositId: depositId,
                editSessionId: this.projectEditModeService.getEditSession()?.id,
                entityId: entityId,
                incomingFiber: specificFiberInformation?.incomingFiber,
                outgoingFiber: specificFiberInformation?.outgoingFiber,
            }),
        );
    }

    private sanitizeFileName(input: string): string {
        return input.replace(/[\s/]/g, '_');
    }

    private sortOpticalPath(a: OpticalPathExtended, b: OpticalPathExtended): number {
        const tiroirA: number = Number(a.tiroir) || 0;
        const tiroirB: number = Number(b.tiroir) || 0;

        if (tiroirA !== tiroirB) {
            return tiroirA - tiroirB;
        }

        const cassetteA: number = Number(a.cassette) || 0;
        const cassetteB: number = Number(b.cassette) || 0;

        if (cassetteA !== cassetteB) {
            return cassetteA - cassetteB;
        }

        const positionA: number = Number(a.position) || 0;
        const positionB: number = Number(b.position) || 0;

        return positionA - positionB;
    }
}
